import { useEffect } from 'react';
import store from 'store';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import IAsync from 'types/IAsync';
import IAuthUser from 'types/IAuthUser';
import IAuthState from 'types/IAuthState';

function setAuthStateAsync(authStateAsync: IAsync<IAuthState>) {
  store.setState({ authStateAsync });
}

function setAuthUser(user: void | IAuthUser) {
  setAuthStateAsync({
    status: 'SUCCESS',
    data: user
      ? {
          authenticated: true,
          user,
        }
      : {
          authenticated: false,
          user: void 0,
        },
  });
}

async function fetchCurrentUser(): Promise<IAuthUser | void> {
  try {
    await getCurrentUser();
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    if (!accessToken) return void 0;
    const idTokenPayload = session.tokens?.idToken?.payload ?? {};
    return {
      accessToken,
      idTokenPayload: {
        given_name: idTokenPayload['given_name'] as string | undefined,
        family_name: idTokenPayload['family_name'] as string | undefined,
        name: idTokenPayload['name'] as string | undefined,
      },
    };
  } catch {
    return void 0;
  }
}

export default function useTrackAuthState() {
  // Check if already authenticated
  useEffect(() => {
    fetchCurrentUser().then(setAuthUser);
  }, []);

  // Listen for change in auth state
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload: { event } }) => {
      switch (event) {
        case 'signedIn': {
          const user = await fetchCurrentUser();
          setAuthUser(user);
          break;
        }
        case 'signedOut':
          setAuthUser(void 0);
          break;
      }
    });

    return unsubscribe;
  }, []);
}
